import type {
  FilledTextFieldProps,
  OutlinedTextFieldProps,
  StandardTextFieldProps,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import MuiTextField from '@mui/material/TextField';
import { type Translation, useTranslation } from '@shared/translation';
import { FieldLabel } from './FieldLabel';

type MuiTextFieldProps =
  | StandardTextFieldProps
  | FilledTextFieldProps
  | OutlinedTextFieldProps;

export type TextFieldProps = Omit<
  MuiTextFieldProps,
  'label' | 'placeholder'
> & {
  label?: Translation;
  placeholder?: Translation;
  loading?: boolean;
  requiredLabel?: Translation;
  showRequiredLabel?: boolean;
};

export function TextField({
  margin = 'normal',
  variant = 'filled',
  label,
  placeholder,
  fullWidth = true,
  loading,
  requiredLabel,
  showRequiredLabel,
  disabled,
  hiddenLabel,
  slotProps,
  ...rest
}: TextFieldProps) {
  const { translate } = useTranslation();
  const hasLabel = Boolean(label);

  return (
    <MuiTextField
      fullWidth={fullWidth}
      margin={margin}
      variant={variant}
      label={
        hasLabel ? (
          <FieldLabel
            label={label}
            required={showRequiredLabel}
            requiredLabel={requiredLabel}
          />
        ) : undefined
      }
      placeholder={translate(placeholder)}
      hiddenLabel={hiddenLabel ?? !hasLabel}
      disabled={loading ? true : disabled}
      {...rest}
      slotProps={{
        ...slotProps,
        input: {
          ...slotProps?.input,
          ...(variant === 'filled' && { disableUnderline: true }),
          ...(loading && {
            endAdornment: (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ),
          }),
        },
      }}
    />
  );
}
